import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import FinanceLayout from "@/components/FinanceLayout";
import { useWorkspace } from "@/context/WorkspaceContext";
import { api, formatApiError } from "@/lib/api";
import { EXPENSE_ITEMS } from "@/lib/finance";
import { toast } from "sonner";

const label = "text-sm font-semibold text-slate-900 mb-2";
const input = "w-full h-12 rounded-xl border border-slate-200 bg-slate-50/40 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500 placeholder:text-slate-400";

export default function OneTimeExpenseNew() {
  const { workspace } = useWorkspace();
  const nav = useNavigate();
  const [firms, setFirms] = useState([]);
  const [f, setF] = useState({ amount: "", firm: "", firm_other_on: false, firm_other: "", item: "", item_other_on: false, item_other: "", is_business: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { (async () => {
    const { data } = await api.get("/finance/firms", { params: { workspace } });
    setFirms(data);
  })(); }, [workspace]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.amount) return setError("Tutar zorunlu.");
    setSaving(true);
    try {
      await api.post("/finance/onetime-expenses", {
        workspace,
        amount: parseFloat(f.amount),
        firm: f.is_business ? "isletme" : (f.firm_other_on ? null : f.firm),
        firm_other: f.is_business ? null : (f.firm_other_on ? f.firm_other : null),
        item: f.item_other_on ? null : f.item,
        item_other: f.item_other_on ? f.item_other : null,
        is_business: f.is_business,
      });
      toast.success("Tek seferlik gider eklendi");
      nav("/finance/monthly-expenses");
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  const Toggle = ({ on, onChange, testId }) => (
    <span className={`relative w-9 h-5 rounded-full transition-colors ${on ? "bg-emerald-600" : "bg-slate-300"}`}>
      <input type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked)} className="opacity-0 absolute inset-0 w-full h-full cursor-pointer" data-testid={testId} />
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${on ? "left-[18px]" : "left-0.5"}`} />
    </span>
  );

  return (
    <FinanceLayout>
      <div className="mb-6 flex items-center gap-3">
        <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50"><ArrowLeft size={16} /></button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Tek Seferlik Gider Ekleme</h1>
          <p className="text-sm text-slate-500 mt-1">Tek seferlik gider bilgilerini girerek kaydedin.</p>
        </div>
      </div>

      <form onSubmit={submit} className="max-w-[720px] mx-auto rounded-2xl border border-slate-100 bg-white p-8" data-testid="oe-form">
        <div className="space-y-6">
          <div>
            <div className={label}>Tutar</div>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₺</span>
              <input type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="Tutar giriniz" className={input + " pl-9"} data-testid="oe-amount" />
            </div>
          </div>
          <div>
            <div className={label}>Hizmet Verilen Firma</div>
            <div className="mb-3 flex items-center gap-3">
              <Toggle on={f.is_business} onChange={(v) => setF({ ...f, is_business: v, firm: v ? "" : f.firm, firm_other_on: v ? false : f.firm_other_on })} testId="oe-business-toggle" />
              <span className="text-sm text-slate-700">İşletme gideri (firma bağımsız)</span>
            </div>
            <select value={f.firm} onChange={(e) => setF({ ...f, firm: e.target.value })} disabled={f.firm_other_on || f.is_business} className={input + ((f.firm_other_on || f.is_business) ? " opacity-50" : "")} data-testid="oe-firm">
              <option value="">Firma seçiniz</option>
              {firms.map((fb) => <option key={fb} value={fb}>{fb}</option>)}
            </select>
            {!f.is_business && (
              <>
                <div className="mt-3 flex items-center gap-3">
                  <Toggle on={f.firm_other_on} onChange={(v) => setF({ ...f, firm_other_on: v })} testId="oe-firm-other-toggle" />
                  <span className="text-sm text-slate-700">Diğer firma</span>
                </div>
                {f.firm_other_on && (
                  <input type="text" value={f.firm_other} onChange={(e) => setF({ ...f, firm_other: e.target.value })} placeholder="Firma adı giriniz" className={input + " mt-3"} data-testid="oe-firm-other" />
                )}
              </>
            )}
          </div>
          <div>
            <div className={label}>Gider Kalemi</div>
            <select value={f.item} onChange={(e) => setF({ ...f, item: e.target.value })} disabled={f.item_other_on} className={input + (f.item_other_on ? " opacity-50" : "")} data-testid="oe-item">
              <option value="">Gider kalemi seçiniz</option>
              {EXPENSE_ITEMS.map((it) => <option key={it} value={it}>{it}</option>)}
            </select>
            <div className="mt-3 flex items-center gap-3">
              <Toggle on={f.item_other_on} onChange={(v) => setF({ ...f, item_other_on: v })} testId="oe-item-other-toggle" />
              <span className="text-sm text-slate-700">Diğer gider kalemi</span>
            </div>
            {f.item_other_on && (
              <input type="text" value={f.item_other} onChange={(e) => setF({ ...f, item_other: e.target.value })} placeholder="Gider kalemi giriniz" className={input + " mt-3"} data-testid="oe-item-other" />
            )}
          </div>
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3" data-testid="oe-error">{error}</div>}
          <button type="submit" disabled={saving} data-testid="oe-save" className="w-full rounded-xl bg-emerald-800 hover:bg-emerald-700 text-white h-12 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
            <Save size={15} /> {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </form>
    </FinanceLayout>
  );
}
